// Myanmar digits ၀–၉ ↔ ASCII. Store ASCII; display per language (NFR-L10N-4).
const MY = '၀၁၂၃၄၅၆၇၈၉';

export function toMyanmarDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => MY[Number(d)] ?? d);
}

export function toAsciiDigits(input: string): string {
  return input.replace(/[၀-၉]/g, (d) => String(MY.indexOf(d)));
}

/** Format integer MMK: 150000 → "150,000 Ks" or "၁၅၀,၀၀၀ ကျပ်" */
export function formatMMK(amount: number, lang: 'my' | 'en' = 'my'): string {
  const grouped = Math.round(amount).toLocaleString('en-US');
  return lang === 'my' ? `${toMyanmarDigits(grouped)} ကျပ်` : `${grouped} Ks`;
}

// Month names are spelled out here rather than taken from the platform: printed
// certificates and prescriptions must look identical on every browser and Android
// version, and ICU differs (e.g. "Sep" vs "Sept").
/** Gregorian month names in Burmese. Myanmar clinics use the Gregorian calendar. */
const MONTHS_MY = ['ဇန်နဝါရီ', 'ဖေဖော်ဝါရီ', 'မတ်', 'ဧပြီ', 'မေ', 'ဇွန်', 'ဇူလိုင်', 'ဩဂုတ်', 'စက်တင်ဘာ', 'အောက်တိုဘာ', 'နိုဝင်ဘာ', 'ဒီဇင်ဘာ'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a date for display or print.
 * Burmese: ၁၁ စက်တင်ဘာ ၂၀၂၆   English: 11 Sep 2026
 * Accepts a Date, an ISO timestamp, or a YYYY-MM-DD date string.
 */
export function formatDate(value: Date | string | null | undefined, lang: 'my' | 'en' = 'my'): string {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(d.getTime())) return '—';
  if (lang === 'my') return `${toMyanmarDigits(d.getDate())} ${MONTHS_MY[d.getMonth()]} ${toMyanmarDigits(d.getFullYear())}`;
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_EN[d.getMonth()]} ${d.getFullYear()}`;
}

/** Time of day, with Myanmar digits when asked. */
export function formatTime(value: Date | string | null | undefined, lang: 'my' | 'en' = 'my'): string {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const s = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  return lang === 'my' ? toMyanmarDigits(s) : s;
}
